import { selMessage } from "@selector/shared";
import { SelMessage } from "@selector/shared/components";
import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { Component } from "react";
import { connect } from "react-redux";
import { Form, type InjectedFormProps, reduxForm, type Validator } from "redux-form";
import { TriggerButton } from "widgets";

import {
  getFormError,
  handleTabInForm,
  type ISelectOption,
  simpleDropdownFieldValidation,
  simpleDropdownMultiSelectFieldValidation,
  simpleFieldValidation,
} from "@/app-utils/form.ts";
import { type IStringAnyMap } from "@/app-utils/type.ts";
import { type GenomeAssembly, type Taxonomy, type UserGroup } from "@/graphql.ts";
import { loadBasicUserGroups } from "@/redux/modules/listing.ts";
import {
  checkGenomeIdentifier,
  checkGenomeName,
  type INCBIAgentPlaybookState,
  type INCBIDownloadOptions,
  loadGenomeAssemblies,
  loadTaxonomyList,
  submitGenomeAssemblyConfiguration,
} from "@/redux/modules/ncbi-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";

import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import { FieldTitle } from "../FieldTitle.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

interface INCBIAgentConfigureDownloadScreenOwnProps extends IPlaybookWizardStepProvidedProps {
  assemblyIndex: number;
  nextAssembly: () => void;
  previousAssembly: () => void;
}

interface INCBIAgentConfigureDownloadScreenAppStateProps {
  selectedGenomeIds?: number[];
  selectedGenomes: GenomeAssembly[];
  downloadOptions: INCBIDownloadOptions | null;
  downloadParamLookup: Record<string, INCBIDownloadOptions>; // map by accession
  genome: GenomeAssembly | null;
  initialValues: Partial<any>;
  taxonomies?: Taxonomy[] | null;
  isLoading: boolean;
  isLoadingAssemblies: boolean;
  userGroups: UserGroup[];
  globalFormError: string;
}

interface INCBIAgentConfigureDownloadScreenDispatchProps {
  loadTaxonomyList: () => void;
  fetchSelectedAssemblies: (ids: number[]) => Promise<any>;
  submitGenomeAssemblyConfiguration: (assemblyAccession: string, options: INCBIDownloadOptions) => void;
  checkGenomeName: (name: string) => Promise<any>;
  checkGenomeIdentifier: (identifier: string) => Promise<any>;
  loadUserGroups: () => Promise<any>;
}

export interface INCBIAgentConfigureDownloadScreenProps
  extends INCBIAgentConfigureDownloadScreenAppStateProps,
    INCBIAgentConfigureDownloadScreenDispatchProps,
    INCBIAgentConfigureDownloadScreenOwnProps,
    InjectedFormProps<any, any>,
    IPlaybookWizardStepInjectedProps {}

interface INCBIAgentConfigureDownloadScreenState {
  isDownloading?: boolean;
  errorMessage?: string;
  // flag to save if the identifier was changed manually
  // if false, we derive it from the name
  identifierModified?: boolean;
}

const KINGDOM_OPTIONS: ISelectOption[] = [
  { label: "Eukaryotes", value: "metazoa" },
  { label: "Prokaryotes", value: "bacteria" },
];

const PUBLIC: ISelectOption = { label: "Public", value: "Public" };

const IDENTIFIER_FORBIDDEN_CHARS_REGEX = new RegExp("[-\\\\:*\"'/\\s]", "gi");
const IDENTIFIER_MULTIPLE_UNDERSCORE_REGEX = new RegExp("_+", "gi");

function createIdentifier(genome: GenomeAssembly) {
  const name = genome.organismName;
  const strings = name.split(" ");
  let newName = name + "_" + genome.assemblyName;
  if (strings && strings.length > 1) {
    newName = name[0] + "_" + strings[1].substring(0, 4) + "_" + genome.assemblyName;
  }
  return newName.replace(/ /g, "_").substring(0, DB_FIELD_MAX_LENGTH.GENOME.IDENTIFIER);
}

function createStrainName(genome: GenomeAssembly): string {
  let strain = genome.intraspecificName;
  let isolate = genome.isolate;
  strain = strain != null && strain.trim().length > 0 ? (strain.includes("=") ? strain.split("=")[1] : strain) : null;
  isolate =
    isolate != null && isolate.trim().length > 0 ? (isolate.includes("=") ? isolate.split("=")[1] : isolate) : null;
  if (isolate === strain) {
    isolate = null;
  }
  return strain != null ? strain + (isolate != null ? ";" + isolate : "") : isolate != null ? isolate : "";
}

const forbiddenCharacterValidation: Validator = (value: string, _allValues?: any, _props?: any) => {
  if (value.includes("%{")) {
    const name = value;
    const forbiddenText = "%{";
    return <SelMessage id={"form.input.placeholder"} values={{ name, forbiddenText }} />;
  }
  return undefined;
};

//[SEL6-2216] Make sure database name does not exceed 20 characters
function createDBName(name: string): string {
  return name.substr(0, DB_FIELD_MAX_LENGTH.DATABASE.MNEMONIC);
}

function createOptions(g: GenomeAssembly, p: INCBIDownloadOptions): INCBIDownloadOptions {
  // p are the previously entered settings, which are applied again after "going back"
  const identifier: string = p ? p.identifier : createIdentifier(g);
  return {
    identifier: identifier,
    name: p ? p.name : null,
    strain: p ? p.strain : createStrainName(g),
    species: p ? p.species : g.organismName,
    database: p ? p.database : createDBName(identifier), // overwritten in handleSubmitData
    taxonomy: p ? p.taxonomy : null,
    kingdom: p ? p.kingdom : null,
    assembly: p ? p.assembly : g.assemblyAccession,
    groups: p ? p.groups : null,
  };
}

function initialize(ncbiAgentState: INCBIAgentPlaybookState, assemblyIndex: number) {
  const selectedGenomes = ncbiAgentState.selectedGenomes;
  const genomeAssembly: GenomeAssembly =
    selectedGenomes != null && selectedGenomes.length > 0 && assemblyIndex >= 0 ? selectedGenomes[assemblyIndex] : null;

  const options =
    genomeAssembly &&
    createOptions(genomeAssembly, ncbiAgentState.downloadParamLookup[genomeAssembly.assemblyAccession]);
  let initialValues = {};

  if (options) {
    const groups =
      ncbiAgentState.lastSelectedGroups && ncbiAgentState.lastSelectedGroups.length > 0
        ? ncbiAgentState.lastSelectedGroups
        : [PUBLIC];
    const kingdom = ncbiAgentState.lastSelectedKingdom;
    const taxonomy = ncbiAgentState.lastSelectedTaxonomy;
    initialValues = {
      [NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_ASSEMBLY]: options.assembly,
      [NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_IDENTIFIER]: options.identifier,
      [NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_NAME]: options.name || options.identifier,
      [NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_SPECIES]: options.species,
      // the following params are defaulted to useful values in case no previous selection is there
      [NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_STRAIN]: options.strain || "-", // if no strain info is present, default it to dash
      [NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_TAXONOMY]: options.taxonomy || taxonomy,
      [NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_KINGDOM]: options.kingdom || kingdom,
      [NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_GROUPS]: options.groups || groups,
    };
  }
  return {
    genomeAssembly: genomeAssembly,
    initialValues: initialValues,
    options: options,
  };
}

function mapStateToProps(
  state: IAppState,
  ownProps: INCBIAgentConfigureDownloadScreenProps,
): INCBIAgentConfigureDownloadScreenAppStateProps {
  const { initialValues, options, genomeAssembly } = initialize(state.ncbiAgent, ownProps.assemblyIndex);
  return {
    genome: genomeAssembly,
    initialValues: initialValues,
    downloadOptions: options,
    selectedGenomeIds: state.ncbiAgent.selectedGenomeIds,
    selectedGenomes: state.ncbiAgent.selectedGenomes,
    isLoading: state.ncbiAgent.loading,
    isLoadingAssemblies: state.ncbiAgent.loadingAssemblies,
    taxonomies: state.ncbiAgent.taxonomies,
    downloadParamLookup: state.ncbiAgent.downloadParamLookup,
    userGroups: state.listing.userGroups,
    globalFormError: getFormError(NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_CONFIGURE_SCREEN, state),
  };
}

function mapDispatchToProps(dispatch): INCBIAgentConfigureDownloadScreenDispatchProps {
  return {
    loadTaxonomyList: () => dispatch(loadTaxonomyList()),
    fetchSelectedAssemblies: (ids: number[]): Promise<any> => dispatch(loadGenomeAssemblies(ids)),
    submitGenomeAssemblyConfiguration: (assemblyAccession: string, options: INCBIDownloadOptions) =>
      dispatch(submitGenomeAssemblyConfiguration(assemblyAccession, options)),
    checkGenomeName: (name: string) => dispatch(checkGenomeName(name)),
    checkGenomeIdentifier: (identifier: string) => dispatch(checkGenomeIdentifier(identifier)),
    loadUserGroups: () => dispatch(loadBasicUserGroups()),
  };
}

class NCBIAgentConfigureDownloadScreenImpl extends Component<
  INCBIAgentConfigureDownloadScreenProps,
  INCBIAgentConfigureDownloadScreenState
> {
  public static NCBI_AGENT_CONFIGURE_SCREEN = "NCBI_AGENT_CONFIGURE_SCREEN";
  public static NCBI_AGENT_DOWNLOAD_NAME = "name";
  public static NCBI_AGENT_DOWNLOAD_IDENTIFIER = "identifier";
  public static NCBI_AGENT_DOWNLOAD_ASSEMBLY = "assembly";
  public static NCBI_AGENT_DOWNLOAD_STRAIN = "strain";
  public static NCBI_AGENT_DOWNLOAD_SPECIES = "species";
  public static NCBI_AGENT_DOWNLOAD_TAXONOMY = "taxonomy";
  public static NCBI_AGENT_DOWNLOAD_KINGDOM = "kingdom";

  public static NCBI_AGENT_DOWNLOAD_GROUPS = "groups";

  constructor(props) {
    super(props);
    this.state = {
      isDownloading: false,
      errorMessage: null,
      identifierModified: false,
    };
  }

  createIdentifierFromName = (_event, newValue: string) => {
    if (!this.state.identifierModified) {
      newValue = newValue.slice(0, DB_FIELD_MAX_LENGTH.GENOME.IDENTIFIER);
      let curatedValue = newValue.replaceAll(IDENTIFIER_FORBIDDEN_CHARS_REGEX, "_");
      curatedValue = curatedValue.replaceAll(IDENTIFIER_MULTIPLE_UNDERSCORE_REGEX, "_");
      this.props.change(NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_IDENTIFIER, curatedValue);
    }
  };

  componentDidMount() {
    this.props.loadTaxonomyList();
    this.props.loadUserGroups();
    this.props.fetchSelectedAssemblies(this.props.selectedGenomeIds);
  }

  componentDidUpdate(
    prevProps: Readonly<INCBIAgentConfigureDownloadScreenProps>,
    _prevState: Readonly<INCBIAgentConfigureDownloadScreenState>,
    _snapshot?: any,
  ): void {
    if (
      this.props.assemblyIndex !== prevProps.assemblyIndex ||
      this.props.selectedGenomes !== prevProps.selectedGenomes
    ) {
      this.props.asyncValidate();
    }
  }

  render() {
    const valid = !!this.props.genome;
    const description: string = valid
      ? this.props.playbookPage?.description
      : "No genome selected. Please go back and select a genome.";
    const taxonomyList: ISelectOption[] = this.createTaxonomyList();
    const genomeName: string = this.createGenomeName();
    const options: ISelectOption[] = this.props.userGroups
      .map((g) => {
        return { label: g.name, value: g.name };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const isLastAssembly = this.props.selectedGenomeIds.length === this.props.assemblyIndex + 1;
    const totalCount: number = this.props.selectedGenomeIds.length;
    const error = this.props.globalFormError || this.props.error;
    const inErrorState = !!error;
    const goPrevious = !(this.props.assemblyIndex + 1 === 1);
    const goNext = !(this.props.assemblyIndex + 1 === totalCount);

    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={{
          ...this.props.playbookPage,
          label: genomeName,
          description: description,
        }}
        wizardFooterData={{
          error: this.state.errorMessage || this.props.globalFormError,
          nextButtonProps: {
            label: isLastAssembly ? `Review & Submit` : `Proceed to Assembly ${this.props.assemblyIndex + 2}`,
            isLoading: this.state.isDownloading || this.props.isLoading || this.props.isLoadingAssemblies,
            onPress: this.props.handleSubmit(isLastAssembly ? this.nextScreen : this.navigateToNextAssembly),
          },
        }}
        tools={
          <div className="playbook--tools_group">
            <TriggerButton
              label={"Previous"}
              onPress={this.props.handleSubmit(this.navigateToPreviousAssembly)}
              isDisabled={!goPrevious}
              icon={"arrow_back"}
              tooltip={inErrorState && goPrevious ? error : undefined}
            />
            <div className="playbook--tools_group--text">
              {this.props.assemblyIndex + 1}/{totalCount}
            </div>
            <TriggerButton
              label={"Next"}
              onPress={this.props.handleSubmit(this.navigateToNextAssembly)}
              isDisabled={!goNext}
              icon={"arrow_forward"}
              tooltip={inErrorState && goNext ? error : undefined}
            />
          </div>
        }
      >
        <Form
          onSubmit={() => {
            /* empty */
          }}
          className="form-horizontal"
        >
          <div style={{ padding: "10px", marginBottom: "-10px" }} onKeyDownCapture={handleTabInForm}>
            {valid && (
              <div className="admin-tools">
                <InputReduxFormField
                  horizontalLayout={true}
                  componentClass="input"
                  name={NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_NAME}
                  label={<FieldTitle label={"Genome name"} mandatory />}
                  placeholder={selMessage({ id: "form.input.placeholder.mandatory" })}
                  validate={[
                    simpleFieldValidation,
                    forbiddenCharacterValidation,
                    NCBIAgentConfigureDownloadScreenImpl.genomeNameFieldValidator,
                  ]}
                  maxLength={DB_FIELD_MAX_LENGTH.GENOME.NAME}
                  component={InputField}
                  onChange={this.createIdentifierFromName}
                />
                <InputReduxFormField
                  horizontalLayout={true}
                  componentClass="input"
                  name={NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_ASSEMBLY}
                  label={<FieldTitle label={"Assembly accession"} />}
                  disabled={true}
                  component={InputField}
                />
                <InputReduxFormField
                  horizontalLayout={true}
                  componentClass="input"
                  name={NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_IDENTIFIER}
                  label={<FieldTitle label={"Genome identifier"} mandatory />}
                  placeholder={selMessage({ id: "form.input.placeholder.mandatory" })}
                  validate={[
                    simpleFieldValidation,
                    forbiddenCharacterValidation,
                    NCBIAgentConfigureDownloadScreenImpl.genomeIdentifierFieldValidator,
                  ]}
                  maxLength={DB_FIELD_MAX_LENGTH.GENOME.IDENTIFIER}
                  component={InputField}
                  onChange={() => {
                    this.setState({ identifierModified: true });
                  }}
                />
                <InputReduxFormField
                  horizontalLayout={true}
                  componentClass="input"
                  name={NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_STRAIN}
                  label={<FieldTitle label={"Strain"} mandatory />}
                  placeholder={selMessage({ id: "form.input.placeholder.mandatory" })}
                  validate={simpleFieldValidation}
                  maxLength={DB_FIELD_MAX_LENGTH.GENOME.STRAIN}
                  component={InputField}
                />
                <InputReduxFormField
                  horizontalLayout={true}
                  componentClass="input"
                  name={NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_SPECIES}
                  label={<FieldTitle label={"Species"} />}
                  placeholder={selMessage({ id: "form.input.placeholder.mandatory" })}
                  validate={simpleFieldValidation}
                  disabled={true}
                  maxLength={DB_FIELD_MAX_LENGTH.GENOME.SPECIES}
                  component={InputField}
                />
                <ReactSelectReduxFormField
                  label={<FieldTitle label={"Taxonomy"} mandatory />}
                  name={NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_TAXONOMY}
                  horizontalLayout={true}
                  options={taxonomyList}
                  validate={simpleDropdownFieldValidation}
                  isLoading={this.props.isLoading}
                  creatable={false}
                  component={ReactSelectField}
                ></ReactSelectReduxFormField>
                <ReactSelectReduxFormField
                  label={<FieldTitle label={"Transcript handling"} mandatory />}
                  name={NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_KINGDOM}
                  horizontalLayout={true}
                  options={KINGDOM_OPTIONS}
                  validate={simpleDropdownFieldValidation}
                  isLoading={this.props.isLoading}
                  creatable={false}
                  component={ReactSelectField}
                ></ReactSelectReduxFormField>
                <ReactSelectReduxFormField
                  name={NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_GROUPS}
                  label={<FieldTitle label={"User groups"} mandatory />}
                  horizontalLayout={true}
                  options={options}
                  validate={simpleDropdownMultiSelectFieldValidation}
                  isLoading={this.props.isLoading}
                  creatable={false}
                  component={ReactSelectField}
                  isMulti={true}
                />
              </div>
            )}
          </div>
        </Form>
      </PlaybookWizardStep>
    );
  }

  private createGenomeName(): string {
    return this.props.genome ? this.props.genome.organismName + " " + this.props.genome.assemblyName : "";
  }

  private createTaxonomyList(): ISelectOption[] {
    return this.props.taxonomies != null
      ? this.props.taxonomies
          .map((e) => {
            return { label: e.name, value: e.name };
          })
          .sort((l, r) => l.label.localeCompare(r.label))
      : [];
  }

  /**
   * Check the identifier contains no spaces, commas,...
   * Make the value mandatory.
   * Reject if it contains the placeholder start %{
   *
   * @param value
   * @param _allValues
   * @param props
   */
  // make it static to make sure we do not user this.props here (use the provided property! 'cause they are not in sync)
  static genomeIdentifierFieldValidator: Validator = (value: string, _allValues?: any, props?: any) => {
    if (/[-*, /\\"']/.test(value)) {
      return "The following characters are not compatible with the Selector genome identifier: * - , [space] \\ / \" '";
    } else if (
      Object.keys(props.downloadParamLookup) // for all existing entered genome parameters
        .filter((key: string) => props.genome.assemblyAccession !== key) // ... if the entry does not correspond to self
        .map((key: string) => props.downloadParamLookup[key]) // ... we get all input options
        .find((downloadOptions: INCBIDownloadOptions) => downloadOptions.identifier === value)
    ) {
      // ... and check that the genome identifier was not already entered previously in this same wizard
      return "This identifier already exists within this wizard";
    }
  };

  /**
   * Allows spaces and commas, and makes the display name mandatory
   * Reject if it contains the placeholder start %{
   * @param value
   * @param _allValues
   * @param props
   */
  static genomeNameFieldValidator: Validator = (value: string, _allValues?: any, props?: any) => {
    if (
      Object.keys(props.downloadParamLookup) // for all existing entered genome parameters
        .filter((key: string) => props.genome.assemblyAccession !== key) // ... if the entry does not correspond to self
        .map((key: string) => props.downloadParamLookup[key]) // ... we get all input options
        .find((downloadOptions: INCBIDownloadOptions) => downloadOptions.name === value)
    ) {
      // ... and check that the genome name was not already entered previously in this same wizard
      return "This name already exists within this wizard";
    }
  };

  handleSubmitData = (values: IStringAnyMap): Promise<any> => {
    const options: INCBIDownloadOptions = {
      assembly: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_ASSEMBLY],
      identifier: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_IDENTIFIER],
      name: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_NAME],
      strain: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_STRAIN],
      species: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_SPECIES],
      database: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_IDENTIFIER].substr(
        0,
        DB_FIELD_MAX_LENGTH.DATABASE.MNEMONIC,
      ), // reuse the name
      taxonomy: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_TAXONOMY],
      kingdom: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_KINGDOM],
      groups: values[NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_GROUPS],
    };

    this.props.submitGenomeAssemblyConfiguration(this.props.genome.assemblyAccession, options);
    return Promise.resolve(); // nothing asynchronous when setting sample data to redux
  };

  navigateToNextAssembly = (values: Record<string, any>): Promise<any> => {
    return this.handleSubmitData(values).then(this.props.nextAssembly);
  };

  navigateToPreviousAssembly = (values: Record<string, any>): Promise<any> => {
    return this.handleSubmitData(values).then(this.props.previousAssembly);
  };

  nextScreen = (values: Record<string, any>): Promise<any> => {
    return this.handleSubmitData(values).then(this.props.proceedWithNextStep);
  };

  static async asyncValidate(values, _dispatch, props: INCBIAgentConfigureDownloadScreenProps) {
    const CLASS = NCBIAgentConfigureDownloadScreenImpl;

    if (!values) {
      return;
    }

    const name = values[CLASS.NCBI_AGENT_DOWNLOAD_NAME];
    if (name) {
      const nameValidationMessage = await props.checkGenomeName(values[CLASS.NCBI_AGENT_DOWNLOAD_NAME]);
      if (nameValidationMessage) {
        throw {
          [CLASS.NCBI_AGENT_DOWNLOAD_NAME]: nameValidationMessage,
        };
      }
    }

    const identifier = values[CLASS.NCBI_AGENT_DOWNLOAD_IDENTIFIER];
    if (identifier) {
      const identifierValidationMessage = await props.checkGenomeIdentifier(
        values[CLASS.NCBI_AGENT_DOWNLOAD_IDENTIFIER],
      );
      if (identifierValidationMessage) {
        throw {
          [CLASS.NCBI_AGENT_DOWNLOAD_IDENTIFIER]: identifierValidationMessage,
        };
      }
    }
  }
}

const NCBIAgentConfigureDownloadScreen = connect<
  INCBIAgentConfigureDownloadScreenAppStateProps,
  INCBIAgentConfigureDownloadScreenDispatchProps,
  INCBIAgentConfigureDownloadScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_CONFIGURE_SCREEN,
    destroyOnUnmount: false,
    asyncValidate: NCBIAgentConfigureDownloadScreenImpl.asyncValidate,
    asyncBlurFields: [
      NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_IDENTIFIER,
      NCBIAgentConfigureDownloadScreenImpl.NCBI_AGENT_DOWNLOAD_NAME,
    ], // fields that trigger validation onBlur
    enableReinitialize: true,
    touchOnChange: true,
    shouldAsyncValidate: () => true,
  })(NCBIAgentConfigureDownloadScreenImpl) as any,
);

export default NCBIAgentConfigureDownloadScreen;
export { NCBIAgentConfigureDownloadScreen };
